import { Bindable, NotifyProperty, nameof } from "bindable-data";
import { INetworkClient } from "../../../../networking/INetworkClient";
import { IBuildingModel } from "./IBuildingModel";
import { GuestBuildingsDto } from "../../../../networking/data/responses/guestbuildings/GuestBuildingDto";
import { UpgradeBuildingModel } from "./upgrade/UpgradeBuildingModel";
import { IUpgradeBuildingModel } from "./upgrade/IUpgradeBuildingModel";
import { BuildingLevelUpDto } from "../../../../networking/data/responses/upgrade/BuildingLevelUpDto";
import { IBuildingRankUpModel } from "../../../popups/buildingRankUp/model/IBuildingRankUpModel";
import { BuildingRankUpModel } from "../../../popups/buildingRankUp/model/BuildingRankUpModel";
import { IUserLevelUpModel } from "../../../popups/userlevelup/model/IUserLevelUpModel";
import { UserLevelUpDto } from "by/gamefactory/cityversetycoon/networking/data/responses/upgrade/UserLevelUpDto";
import { UserLevelUpModel } from "../../../popups/userlevelup/model/UserLevelUpModel";
import { PlayerDto } from "by/gamefactory/cityversetycoon/networking/data/responses/user/PlayerDto";
import { BuildingUpgradesDto } from "../../../../networking/data/responses/upgrade/BuildingUpgradesDto";
import { GuestBuilding } from "./GuestBuilding";
import { LandlordData } from "./LandlordData";
import { FixResult } from "./upgrade/FixResult";
import { UpgradeBuildingItemResultDto } from "../../../../networking/data/responses/upgrade/UpgradeBuildingItemResultDto";
import { UpgradeResult } from "./upgrade/UpgradeResult";
import { FixBuildingItemResultDto } from "../../../../networking/data/responses/upgrade/FixBuildingItemResultDto";
import { PrepareMintResult } from "../../../popups/marketplace/main/models/building/PrepareMintResult";
import { PurchaseHistory } from "../../../popups/marketplace/main/models/building/PurchaseHistory";
import { ILogger } from "../../../../logger/ILogger";

export class BuildingModel extends Bindable implements IBuildingModel {
    private readonly networkClient: INetworkClient;
    private readonly logger: ILogger;

    public readonly id: NotifyProperty<string>;
    public readonly address: NotifyProperty<string>;
    public readonly level: NotifyProperty<number>;
    public readonly nextLevel: NotifyProperty<number>;
    public readonly imageUrl: NotifyProperty<string>;
    public readonly area: NotifyProperty<number>;
    public readonly city: NotifyProperty<string>;
    public readonly costPerSqft: NotifyProperty<number>;
    public readonly sqft: NotifyProperty<number>;
    public readonly floors: NotifyProperty<number>;
    public readonly borough: NotifyProperty<string>;
    public readonly zipCode: NotifyProperty<string>;
    public readonly upgradePointsBalance: NotifyProperty<number>;
    public readonly requiredUpgradePoints: NotifyProperty<number>;
    public readonly visitCostInCoinsUpTo: NotifyProperty<number>;
    public readonly landlordData: NotifyProperty<LandlordData>;
    public readonly piggyClubNftMultiplier: NotifyProperty<number>;
    public readonly piggyClubImageUrl: NotifyProperty<string>;
    public readonly piggyMultiplier: NotifyProperty<number>;
    public readonly priceInUsd: NotifyProperty<number>;
    public readonly priceInEth: NotifyProperty<number>;
    public readonly marketplaceId: NotifyProperty<string>;
    public readonly tokenId: NotifyProperty<string>;
    public readonly minBuildingLevelToAddToMp: NotifyProperty<number>;
    public readonly buildingLevelUpModel: NotifyProperty<IBuildingRankUpModel>;
    public readonly userLevelUpModel: NotifyProperty<IUserLevelUpModel>;
    public readonly upgrades: Array<IUpgradeBuildingModel>;
    public readonly needToFix: NotifyProperty<boolean>;
    public readonly purchaseHistory: NotifyProperty<Array<PurchaseHistory>>;
    private upgradeFixResult: UpgradeResult | FixResult;

    get fromUpgrade(): boolean {
        return true;
    }

    public get upgradeOrFixResult(): UpgradeResult | FixResult {
        return this.upgradeFixResult;
    }

    public get canBeFixed(): boolean {
        return this.needToFix.value;
    }

    private constructor(
        networkClient: INetworkClient,
        guestBuilding: GuestBuilding,
        upgrades: Array<IUpgradeBuildingModel>,
        logger: ILogger
    ) {
        super();
        this.networkClient = networkClient;
        this.logger = logger;
        this.id = new NotifyProperty<string>(this, nameof(this, "id"), guestBuilding.id);
        this.address = new NotifyProperty<string>(this, nameof(this, "address"), guestBuilding.address);
        this.level = new NotifyProperty<number>(this, nameof(this, "level"), guestBuilding.level);
        this.nextLevel = new NotifyProperty<number>(this, nameof(this, "nextLevel"), guestBuilding.nextLevel || 0);
        this.imageUrl = new NotifyProperty<string>(this, nameof(this, "imageUrl"), guestBuilding.imageUrl);
        this.area = new NotifyProperty<number>(this, nameof(this, "area"), guestBuilding.area);
        this.city = new NotifyProperty<string>(this, nameof(this, "city"), guestBuilding.city);
        this.costPerSqft = new NotifyProperty<number>(this, nameof(this, "costPerSqft"), guestBuilding.costPerSqft);
        this.sqft = new NotifyProperty<number>(this, nameof(this, "sqft"), guestBuilding.sqft);
        this.floors = new NotifyProperty<number>(this, nameof(this, "floors"), guestBuilding.floors);
        this.borough = new NotifyProperty<string>(this, nameof(this, "borough"), guestBuilding.borough);
        this.zipCode = new NotifyProperty<string>(this, nameof(this, "zipCode"), guestBuilding.zipCode);
        this.upgradePointsBalance = new NotifyProperty<number>(this, nameof(this, "upgradePointsBalance"), guestBuilding.upgradePointsBalance);
        this.requiredUpgradePoints = new NotifyProperty<number>(this, nameof(this, "requiredUpgradePoints"), guestBuilding.requiredUpgradePoints || 0);
        this.visitCostInCoinsUpTo = new NotifyProperty<number>(this, nameof(this, "visitCostInCoinsUpTo"), guestBuilding.visitCostInCoinsUpTo);
        this.landlordData = new NotifyProperty<LandlordData>(this, nameof(this, "landlordData"), guestBuilding.landlord);
        this.piggyClubNftMultiplier = new NotifyProperty<number>(this, nameof(this, "piggyClubNftMultiplier"), guestBuilding.piggyClubNftMultiplier);
        this.piggyClubImageUrl = new NotifyProperty<string>(this, nameof(this, "piggyClubImageUrl"), guestBuilding.piggyClubImageUrl);
        this.piggyMultiplier = new NotifyProperty<number>(this, nameof(this, "piggyMultiplier"), guestBuilding.piggyMultiplier);
        this.priceInUsd = new NotifyProperty<number>(this, nameof(this, "priceInUsd"), guestBuilding.priceInUsd);
        this.priceInEth = new NotifyProperty<number>(this, nameof(this, "priceInEth"), guestBuilding.priceInEth);
        this.marketplaceId = new NotifyProperty<string>(this, nameof(this, "marketplaceId"), guestBuilding.marketplaceId);
        this.tokenId = new NotifyProperty<string>(this, nameof(this, "tokenId"), guestBuilding.tokenId);
        this.minBuildingLevelToAddToMp = new NotifyProperty<number>(this, nameof(this, "minBuildingLevelToAddToMp"), guestBuilding.minBuildingLevelToAddToMp);
        this.buildingLevelUpModel = new NotifyProperty<IBuildingRankUpModel>(this, nameof(this, "buildingLevelUpModel"), undefined);
        this.userLevelUpModel = new NotifyProperty<IUserLevelUpModel>(this, nameof(this, "userLevelUpModel"), undefined);
        this.needToFix = new NotifyProperty<boolean>(this, nameof(this, "needToFix"), guestBuilding.needToFix);
        this.purchaseHistory = new NotifyProperty<Array<PurchaseHistory>>(this, nameof(this, "needToFix"), guestBuilding.purchaseHistory);
        this.upgrades = upgrades;
    }

    public async upgrade(upgradeItemId: string, perCoins: boolean): Promise<UpgradeBuildingItemResultDto> {
        const upgradeItem = this.upgrades.find((item) => item.id.value == upgradeItemId);
        if (upgradeItem == undefined) {
            throw new Error(`Building does not have upgrade with id ${upgradeItemId}.`);
        }
        const upgradeBuildingItemResultDto = await upgradeItem.upgrade(this.id.value, perCoins);
        this.upgrades.forEach(upgradeModel => upgradeModel.updateValuesFromUpgradeOrFix(upgradeBuildingItemResultDto.upgrades));
        this.updateValuesFromGuestBuildingsDto(upgradeBuildingItemResultDto.building);
        this.updateBuildingLevelUpModel(upgradeBuildingItemResultDto.buildingLevelUp);
        this.updateUserLevelUpModel(upgradeBuildingItemResultDto.guestLevelUp, upgradeBuildingItemResultDto.guest);
        if (upgradeBuildingItemResultDto.upgradeResult == undefined) {
            throw new Error("Building does not have upgrade with upgradeResult.");
        }
        this.upgradeFixResult = new UpgradeResult(upgradeBuildingItemResultDto.upgradeResult);
        return upgradeBuildingItemResultDto;
    }

    public async fix(fixItemId: string, perCoins: boolean): Promise<FixBuildingItemResultDto> {
        const fixItem = this.upgrades.find((item) => item.id.value == fixItemId);
        if (fixItem == undefined) {
            throw new Error(`Building does not have fix with id ${fixItemId}.`);
        }
        const fixBuildingItemResultDto = await fixItem.fix(this.id.value, perCoins);
        this.upgrades.forEach(upgradeModel => upgradeModel.updateValuesFromUpgradeOrFix(fixBuildingItemResultDto.upgrades));
        this.updateValuesFromGuestBuildingsDto(fixBuildingItemResultDto.building);
        this.updateBuildingLevelUpModel(fixBuildingItemResultDto.buildingLevelUp);
        this.updateUserLevelUpModel(fixBuildingItemResultDto.guestLevelUp, fixBuildingItemResultDto.guest);
        if (fixBuildingItemResultDto.fixResult == undefined) {
            throw new Error("Building does not have upgrade with fixResult.");
        }
        this.upgradeFixResult = new FixResult(fixBuildingItemResultDto.fixResult);
        return fixBuildingItemResultDto;
    }

    public async prepareMint(): Promise<PrepareMintResult> {
        const result = await this.networkClient.guestBuildingPrepareMint(this.id.value);
        return new PrepareMintResult(result);
    }

    private updateBuildingLevelUpModel(dto: BuildingLevelUpDto): void {
        const buildLevelUpModel = dto == undefined ? undefined : new BuildingRankUpModel(dto, this.level.value);
        this.buildingLevelUpModel.setValue(buildLevelUpModel);
    }

    private updateUserLevelUpModel(dto: UserLevelUpDto, playerDto: PlayerDto): void {
        const userLevelUpModel = dto == undefined ? undefined : new UserLevelUpModel(dto, playerDto);
        this.userLevelUpModel.setValue(userLevelUpModel);
    }

    private updateValuesFromGuestBuildingsDto(dto: GuestBuildingsDto): void {
        const guestBuilding = new GuestBuilding(dto);
        this.id.setValue(guestBuilding.id);
        this.address.setValue(guestBuilding.address);
        this.level.setValue(guestBuilding.level);
        this.nextLevel.setValue(guestBuilding.nextLevel || 0);
        this.imageUrl.setValue(guestBuilding.imageUrl);
        this.area.setValue(guestBuilding.area);
        this.city.setValue(guestBuilding.city);
        this.costPerSqft.setValue(guestBuilding.costPerSqft);
        this.sqft.setValue(guestBuilding.sqft);
        this.floors.setValue(guestBuilding.floors);
        this.borough.setValue(guestBuilding.borough);
        this.zipCode.setValue(guestBuilding.zipCode);
        this.upgradePointsBalance.setValue(guestBuilding.upgradePointsBalance);
        this.requiredUpgradePoints.setValue(guestBuilding.requiredUpgradePoints || 0);
        this.visitCostInCoinsUpTo.setValue(guestBuilding.visitCostInCoinsUpTo);
        this.landlordData.setValue(guestBuilding.landlord);
        this.piggyMultiplier.setValue(guestBuilding.piggyMultiplier);
        this.priceInUsd.setValue(guestBuilding.priceInUsd);
        this.priceInEth.setValue(guestBuilding.priceInEth);
        this.marketplaceId.setValue(guestBuilding.marketplaceId);
        this.tokenId.setValue(guestBuilding.tokenId);
        this.minBuildingLevelToAddToMp.setValue(guestBuilding.minBuildingLevelToAddToMp);
        this.needToFix.setValue(guestBuilding.needToFix);
    }

    public static async create(networkClient: INetworkClient, dto: GuestBuildingsDto, logger: ILogger, withUpgrades: boolean = true): Promise<BuildingModel> {
        try {
            logger.debug(`[BUILDINGS WORKFLOW] Create building model - ${dto.id} (with upgrades - ${withUpgrades})`);
            const guestBuilding = new GuestBuilding(dto);
            const upgrades: Array<IUpgradeBuildingModel> = [];
            if (withUpgrades) {
                logger.debug(`[BUILDINGS WORKFLOW] Request upgrades for ${dto.id}`);
                const upgradesDto = await this.getUpgrades(networkClient, dto.id);
                for (const upgradeItemDto of upgradesDto.items) {
                    const upgradeBuildingModel = new UpgradeBuildingModel(networkClient, upgradeItemDto);
                    upgrades.push(upgradeBuildingModel);
                }
            }
            return new BuildingModel(networkClient, guestBuilding, upgrades, logger);
        }
        catch (e) {
            logger.error(`[BUILDINGS WORKFLOW] Exception on request upgrades:`, e);
        }
    }

    private static async getUpgrades(networkClient: INetworkClient, buildingId: string): Promise<BuildingUpgradesDto> {
        const buildingUpgradesDto = await networkClient.getBuildingUpgrades(buildingId);
        if (buildingUpgradesDto.items == undefined) {
            throw new Error("Failed to create guest building upgrades.");
        }
        return buildingUpgradesDto;
    }

}
